import React, { useState, useEffect } from 'react';
import API from '../../utils/api';

const Instruments = () => {
  const [activeTab, setActiveTab] = useState('borrowings');
  const [instruments, setInstruments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [borrowings, setBorrowings] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Add instrument modal
  const [showAddInstrument, setShowAddInstrument] = useState(false);
  const [newInstrument, setNewInstrument] = useState({
    code: '', category_id: '', name: ''
  });

  // Borrow modal
  const [showBorrow, setShowBorrow] = useState(false);
  const [borrowStep, setBorrowStep] = useState(1);
  const [borrowSearch, setBorrowSearch] = useState('');
  const [borrowMember, setBorrowMember] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [availableInstruments, setAvailableInstruments] = useState([]);
  const [selectedInstruments, setSelectedInstruments] = useState([]);
  const [deadline, setDeadline] = useState('');

  // Damage modal
  const [showDamage, setShowDamage] = useState(false);
  const [damageData, setDamageData] = useState({
    instrument_id: '', description: ''
  });

  // Add category modal
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    fetchCategories();
    fetchInstruments();
    fetchBorrowings();
    fetchMembers();
  }, []);

  const showMsg = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 4000);
  };

  const fetchCategories = async () => {
    try {
      const res = await API.get('/instruments/categories');
      setCategories(res.data);
    } catch (error) { console.error(error); }
  };

  const fetchInstruments = async () => {
    try {
      setLoading(true);
      const res = await API.get('/instruments');
      setInstruments(res.data);
    } catch (error) { console.error(error); }
    setLoading(false);
  };

  const fetchBorrowings = async () => {
    try {
      const res = await API.get('/instruments/borrowings');
      setBorrowings(res.data);
    } catch (error) { console.error(error); }
  };

  const fetchMembers = async () => {
    try {
      const res = await API.get('/members');
      setMembers(res.data.filter(m => m.status === 'approved'));
    } catch (error) { console.error(error); }
  };

  const handleAddInstrument = async () => {
    if (!newInstrument.code || !newInstrument.category_id || !newInstrument.name) {
      showMsg('Please fill in all fields!', 'error');
      return;
    }
    try {
      await API.post('/instruments', newInstrument);
      showMsg('✅ Instrument added!', 'success');
      setShowAddInstrument(false);
      setNewInstrument({ code: '', category_id: '', name: '' });
      fetchInstruments();
    } catch (error) {
      showMsg(error.response?.data?.message || 'Error!', 'error');
    }
  };

  const handleRemoveInstrument = async (id) => {
    if (!window.confirm('Remove this instrument?')) return;
    try {
      await API.delete(`/instruments/${id}`);
      showMsg('✅ Instrument removed!', 'success');
      fetchInstruments();
    } catch (error) {
      showMsg(error.response?.data?.message || 'Error!', 'error');
    }
  };

  const handleCategoryChange = (category_id) => {
    setSelectedCategory(category_id);
    const filtered = instruments.filter(
      i => i.category_id === parseInt(category_id) && i.is_available
    );
    setAvailableInstruments(filtered);
  };

  const toggleInstrumentSelect = (instrument) => {
    const exists = selectedInstruments.find(i => i.id === instrument.id);
    if (exists) {
      setSelectedInstruments(selectedInstruments.filter(i => i.id !== instrument.id));
    } else {
      setSelectedInstruments([...selectedInstruments, instrument]);
    }
  };

  const handleBorrow = async () => {
    if (!deadline) {
      showMsg('Please set a deadline!', 'error');
      return;
    }
    if (selectedInstruments.length === 0) {
      showMsg('Please select at least one instrument!', 'error');
      return;
    }
    try {
      for (const instrument of selectedInstruments) {
        await API.post('/instruments/borrow', {
          member_id: borrowMember.id,
          instrument_id: instrument.id,
          deadline
        });
      }
      showMsg(`✅ ${selectedInstruments.length} instrument(s) borrowed!`, 'success');
      setShowBorrow(false);
      setBorrowStep(1);
      setBorrowMember(null);
      setSelectedInstruments([]);
      setDeadline('');
      fetchInstruments();
      fetchBorrowings();
    } catch (error) {
      showMsg(error.response?.data?.message || 'Error!', 'error');
    }
  };

  const handleApproveReturn = async (id) => {
    try {
      await API.put(`/instruments/borrowings/${id}/approve-return`);
      showMsg('✅ Return approved!', 'success');
      fetchBorrowings();
      fetchInstruments();
    } catch (error) {
      showMsg('Error!', 'error');
    }
  };

  const handleRejectReturn = async (id) => {
    try {
      await API.put(`/instruments/borrowings/${id}/reject-return`);
      showMsg('✅ Return rejected!', 'success');
      fetchBorrowings();
    } catch (error) {
      showMsg('Error!', 'error');
    }
  };

  const handleReportDamage = async () => {
    if (!damageData.instrument_id || !damageData.description) {
      showMsg('Please fill in all fields!', 'error');
      return;
    }
    try {
      await API.post('/instruments/damage', damageData);
      showMsg('✅ Damage reported!', 'success');
      setShowDamage(false);
      setDamageData({ instrument_id: '', description: '' });
      fetchInstruments();
    } catch (error) {
      showMsg('Error!', 'error');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory) return;
    try {
      await API.post('/instruments/categories', { name: newCategory });
      showMsg('✅ Category added!', 'success');
      setShowAddCategory(false);
      setNewCategory('');
      fetchCategories();
    } catch (error) {
      showMsg('Error!', 'error');
    }
  };

  const filteredMembers = members.filter(m =>
    m.first_name.toLowerCase().includes(borrowSearch.toLowerCase()) ||
    m.last_name.toLowerCase().includes(borrowSearch.toLowerCase()) ||
    m.school_id.includes(borrowSearch)
  );

  const returnRequests = borrowings.filter(b => b.status === 'return_requested');
  const activeBorrowings = borrowings.filter(b =>
    b.status === 'borrowed' || b.status === 'overdue'
  );

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>🎵 Instruments</h2>
          <p style={styles.subtitle}>Manage instrument borrowings and inventory</p>
        </div>
        <div style={styles.headerBtns}>
          <button style={styles.addBtn} onClick={() => setShowBorrow(true)}>
            + Borrow Instrument
          </button>
          <button style={styles.secondaryBtn}
            onClick={() => setShowAddInstrument(true)}>
            + Add Instrument
          </button>
          <button style={styles.secondaryBtn}
            onClick={() => setShowDamage(true)}>
            ⚠️ Report Damage
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div style={messageType === 'success' ? styles.successMsg : styles.errorMsg}>
          {message}
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabs}>
        {[
          { id: 'borrowings', label: `📋 Active Borrowings (${activeBorrowings.length})` },
          { id: 'returns', label: `🔄 Return Requests (${returnRequests.length})` },
          { id: 'inventory', label: `🎵 All Instruments (${instruments.length})` },
        ].map(tab => (
          <button
            key={tab.id}
            style={activeTab === tab.id ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ACTIVE BORROWINGS */}
      {activeTab === 'borrowings' && (
        <div style={styles.tableBox}>
          {activeBorrowings.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyIcon}>🎵</p>
              <p style={styles.emptyText}>No active borrowings!</p>
            </div>
          ) : (
            <div style={styles.reportTable}>
              <div style={styles.tableHeader}>
                <span style={styles.tableCol}>Member</span>
                <span style={styles.tableCol}>Instrument</span>
                <span style={styles.tableCol}>Code</span>
                <span style={styles.tableCol}>Borrowed</span>
                <span style={styles.tableCol}>Deadline</span>
                <span style={styles.tableCol}>Status</span>
              </div>
              {activeBorrowings.map(b => {
                const isOverdue = new Date(b.deadline) < new Date();
                return (
                  <div key={b.id} style={styles.tableRow}>
                    <span style={styles.tableCol}>
                      {b.first_name} {b.last_name}
                      <br />
                      <small style={styles.smallText}>{b.school_id}</small>
                    </span>
                    <span style={styles.tableCol}>
                      {b.category_name}
                      <br />
                      <small style={styles.smallText}>{b.instrument_name}</small>
                    </span>
                    <span style={styles.tableCol}>{b.instrument_code}</span>
                    <span style={styles.tableCol}>
                      {new Date(b.borrowed_at).toLocaleDateString('en-PH')}
                    </span>
                    <span style={styles.tableCol}>
                      <span style={{
                        color: isOverdue ? '#e74c3c' : '#333',
                        fontWeight: isOverdue ? 'bold' : 'normal'
                      }}>
                        {new Date(b.deadline).toLocaleDateString('en-PH')}
                        {isOverdue && ' ⚠️'}
                      </span>
                    </span>
                    <span style={styles.tableCol}>
                      <span style={{
                        ...styles.statusPill,
                        background: isOverdue ? '#ffe0e0' : '#fff3e0',
                        color: isOverdue ? '#cc0000' : '#cc7700',
                      }}>
                        {isOverdue ? 'overdue' : 'borrowed'}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* RETURN REQUESTS */}
      {activeTab === 'returns' && (
        <div style={styles.tableBox}>
          {returnRequests.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyIcon}>🔄</p>
              <p style={styles.emptyText}>No return requests!</p>
            </div>
          ) : (
            returnRequests.map(b => (
              <div key={b.id} style={styles.returnCard}>
                <div style={styles.returnInfo}>
                  <p style={styles.returnName}>
                    {b.first_name} {b.last_name}
                    <span style={styles.returnId}> · {b.school_id}</span>
                  </p>
                  <p style={styles.returnInstrument}>
                    🎵 {b.category_name} — {b.instrument_name}
                    <span style={styles.returnCode}> [{b.instrument_code}]</span>
                  </p>
                  <p style={styles.returnDate}>
                    Borrowed: {new Date(b.borrowed_at).toLocaleDateString('en-PH')} ·
                    Deadline: {new Date(b.deadline).toLocaleDateString('en-PH')}
                  </p>
                </div>
                <div style={styles.returnActions}>
                  <button style={styles.approveBtn}
                    onClick={() => handleApproveReturn(b.id)}>
                    ✓ Approve
                  </button>
                  <button style={styles.rejectBtn}
                    onClick={() => handleRejectReturn(b.id)}>
                    ✕ Reject
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* INVENTORY */}
      {activeTab === 'inventory' && (
        <div style={styles.tableBox}>
          <div style={styles.inventoryHeader}>
            <button style={styles.secondaryBtn}
              onClick={() => setShowAddCategory(true)}>
              + Add Category
            </button>
          </div>
          {loading ? (
            <p style={styles.loadingText}>Loading...</p>
          ) : instruments.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyIcon}>🎵</p>
              <p style={styles.emptyText}>No instruments in inventory!</p>
            </div>
          ) : (
            <div style={styles.reportTable}>
              <div style={styles.tableHeader}>
                <span style={styles.tableCol}>Code</span>
                <span style={styles.tableCol}>Category</span>
                <span style={styles.tableCol}>Name</span>
                <span style={styles.tableCol}>Condition</span>
                <span style={styles.tableCol}>Status</span>
                <span style={styles.tableCol}>Action</span>
              </div>
              {instruments.map(i => (
                <div key={i.id} style={styles.tableRow}>
                  <span style={styles.tableCol}>
                    <strong>{i.code}</strong>
                  </span>
                  <span style={styles.tableCol}>{i.category_name}</span>
                  <span style={styles.tableCol}>{i.name}</span>
                  <span style={styles.tableCol}>
                    <span style={{
                      ...styles.statusPill,
                      background: i.condition_status === 'good' ? '#e0ffe0' :
                        i.condition_status === 'damaged' ? '#ffe0e0' : '#fff3e0',
                      color: i.condition_status === 'good' ? '#006600' :
                        i.condition_status === 'damaged' ? '#cc0000' : '#cc7700',
                    }}>
                      {i.condition_status}
                    </span>
                  </span>
                  <span style={styles.tableCol}>
                    <span style={{
                      ...styles.statusPill,
                      background: i.is_available ? '#e0ffe0' : '#ffe0e0',
                      color: i.is_available ? '#006600' : '#cc0000',
                    }}>
                      {i.is_available ? 'available' : 'borrowed'}
                    </span>
                  </span>
                  <span style={styles.tableCol}>
                    <button style={styles.removeBtn}
                      onClick={() => handleRemoveInstrument(i.id)}>
                      🗑️
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ADD INSTRUMENT MODAL */}
      {showAddInstrument && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>🎵 Add New Instrument</h3>
            <div style={styles.field}>
              <label style={styles.label}>Category *</label>
              <select style={styles.input}
                value={newInstrument.category_id}
                onChange={e => setNewInstrument({
                  ...newInstrument, category_id: e.target.value
                })}>
                <option value="">Select category</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Instrument Name *</label>
              <input style={styles.input} type="text"
                placeholder="ex. Kulintang 1"
                value={newInstrument.name}
                onChange={e => setNewInstrument({
                  ...newInstrument, name: e.target.value
                })} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Code *</label>
              <input style={styles.input} type="text"
                placeholder="ex. PRC-KLT-001"
                value={newInstrument.code}
                onChange={e => setNewInstrument({
                  ...newInstrument, code: e.target.value
                })} />
            </div>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn}
                onClick={() => setShowAddInstrument(false)}>Cancel</button>
              <button style={styles.doneBtn}
                onClick={handleAddInstrument}>Add Instrument</button>
            </div>
          </div>
        </div>
      )}

      {/* BORROW MODAL */}
      {showBorrow && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>
              {borrowStep === 1 ? '👤 Select Member' :
                borrowStep === 2 ? '🎵 Select Instruments' :
                  '📅 Set Deadline'}
            </h3>

            {/* Step 1 */}
            {borrowStep === 1 && (
              <div>
                <input style={styles.input} type="text"
                  placeholder="Search by name or ID..."
                  value={borrowSearch}
                  onChange={e => setBorrowSearch(e.target.value)} />
                <div style={styles.memberList}>
                  {filteredMembers.slice(0, 8).map(m => (
                    <button key={m.id}
                      style={{
                        ...styles.memberSelectBtn,
                        background: borrowMember?.id === m.id ? '#e8f0fe' : 'white',
                        border: borrowMember?.id === m.id ?
                          '2px solid #0f3460' : '1.5px solid #ddd',
                      }}
                      onClick={() => setBorrowMember(m)}>
                      <div style={styles.memberAvatar}>
                        {m.first_name?.charAt(0)}{m.last_name?.charAt(0)}
                      </div>
                      <div>
                        <p style={styles.memberName2}>
                          {m.first_name} {m.last_name}
                        </p>
                        <p style={styles.memberId2}>{m.school_id}</p>
                      </div>
                    </button>
                  ))}
                </div>
                <div style={styles.modalButtons}>
                  <button style={styles.cancelBtn}
                    onClick={() => {
                      setShowBorrow(false);
                      setBorrowMember(null);
                    }}>Cancel</button>
                  <button
                    style={borrowMember ? styles.doneBtn : styles.doneBtnDisabled}
                    disabled={!borrowMember}
                    onClick={() => setBorrowStep(2)}>
                    Next →
                  </button>
                </div>
              </div>
            )}

            {/* Step 2 */}
            {borrowStep === 2 && (
              <div>
                <p style={styles.borrowingFor}>
                  Borrowing for: <strong>
                    {borrowMember?.first_name} {borrowMember?.last_name}
                  </strong>
                </p>
                <div style={styles.field}>
                  <label style={styles.label}>Category</label>
                  <select style={styles.input}
                    value={selectedCategory}
                    onChange={e => handleCategoryChange(e.target.value)}>
                    <option value="">Select category</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                {availableInstruments.length > 0 && (
                  <div style={styles.instrumentList}>
                    {availableInstruments.map(i => {
                      const isSelected = selectedInstruments.find(s => s.id === i.id);
                      return (
                        <button key={i.id}
                          style={{
                            ...styles.instrumentSelectBtn,
                            background: isSelected ? '#e8f0fe' : 'white',
                            border: isSelected ?
                              '2px solid #0f3460' : '1.5px solid #ddd',
                          }}
                          onClick={() => toggleInstrumentSelect(i)}>
                          <span style={styles.instrumentCode}>{i.code}</span>
                          <span style={styles.instrumentName2}>{i.name}</span>
                          {isSelected && <span style={styles.checkMark}>✓</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
                {selectedInstruments.length > 0 && (
                  <div style={styles.selectedSummary}>
                    <p style={styles.selectedTitle}>
                      Selected: {selectedInstruments.length} instrument(s)
                    </p>
                    {selectedInstruments.map(i => (
                      <p key={i.id} style={styles.selectedItem}>
                        · {i.code} — {i.name}
                      </p>
                    ))}
                  </div>
                )}
                <div style={styles.modalButtons}>
                  <button style={styles.cancelBtn}
                    onClick={() => setBorrowStep(1)}>← Back</button>
                  <button
                    style={selectedInstruments.length > 0 ?
                      styles.doneBtn : styles.doneBtnDisabled}
                    disabled={selectedInstruments.length === 0}
                    onClick={() => setBorrowStep(3)}>
                    Next →
                  </button>
                </div>
              </div>
            )}

            {/* Step 3 */}
            {borrowStep === 3 && (
              <div>
                <p style={styles.borrowingFor}>
                  Member: <strong>
                    {borrowMember?.first_name} {borrowMember?.last_name}
                  </strong>
                </p>
                <p style={styles.borrowingFor}>
                  Instruments: <strong>{selectedInstruments.length} item(s)</strong>
                </p>
                <div style={styles.field}>
                  <label style={styles.label}>Return Deadline *</label>
                  <input style={styles.input} type="date"
                    value={deadline}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={e => setDeadline(e.target.value)} />
                </div>
                <div style={styles.modalButtons}>
                  <button style={styles.cancelBtn}
                    onClick={() => setBorrowStep(2)}>← Back</button>
                  <button style={styles.doneBtn} onClick={handleBorrow}>
                    ✓ Confirm Borrow
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* DAMAGE REPORT MODAL */}
      {showDamage && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>⚠️ Report Damage</h3>
            <div style={styles.field}>
              <label style={styles.label}>Instrument *</label>
              <select style={styles.input}
                value={damageData.instrument_id}
                onChange={e => setDamageData({
                  ...damageData, instrument_id: e.target.value
                })}>
                <option value="">Select instrument</option>
                {instruments.map(i => (
                  <option key={i.id} value={i.id}>
                    [{i.code}] {i.category_name} — {i.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Description *</label>
              <textarea
                style={{ ...styles.input, height: '100px', resize: 'vertical' }}
                placeholder="Describe the damage..."
                value={damageData.description}
                onChange={e => setDamageData({
                  ...damageData, description: e.target.value
                })} />
            </div>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn}
                onClick={() => setShowDamage(false)}>Cancel</button>
              <button style={styles.doneBtn}
                onClick={handleReportDamage}>Submit Report</button>
            </div>
          </div>
        </div>
      )}

      {/* ADD CATEGORY MODAL */}
      {showAddCategory && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, maxWidth: '360px' }}>
            <h3 style={styles.modalTitle}>+ Add Category</h3>
            <div style={styles.field}>
              <label style={styles.label}>Category Name *</label>
              <input style={styles.input} type="text"
                placeholder="ex. Traditional, Modern..."
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)} />
            </div>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn}
                onClick={() => setShowAddCategory(false)}>Cancel</button>
              <button style={styles.doneBtn}
                onClick={handleAddCategory}>Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '0' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '10px',
  },
  title: { margin: '0 0 5px 0', fontSize: '22px', color: '#333' },
  subtitle: { margin: 0, color: '#888', fontSize: '13px' },
  headerBtns: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  addBtn: {
    padding: '10px 18px',
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    color: 'white', border: 'none', borderRadius: '10px',
    cursor: 'pointer', fontWeight: 'bold', fontSize: '13px',
  },
  secondaryBtn: {
    padding: '10px 18px', background: 'white',
    color: '#0f3460', border: '1.5px solid #0f3460',
    borderRadius: '10px', cursor: 'pointer',
    fontWeight: 'bold', fontSize: '13px',
  },
  successMsg: {
    background: '#e0ffe0', color: '#006600',
    padding: '12px', borderRadius: '8px',
    marginBottom: '15px', fontSize: '14px',
  },
  errorMsg: {
    background: '#ffe0e0', color: '#cc0000',
    padding: '12px', borderRadius: '8px',
    marginBottom: '15px', fontSize: '14px',
  },
  tabs: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  tab: {
    padding: '8px 18px', background: 'white',
    border: '1.5px solid #ddd', borderRadius: '20px',
    cursor: 'pointer', fontSize: '13px', color: '#666',
  },
  tabActive: {
    padding: '8px 18px', background: '#0f3460',
    border: '1.5px solid #0f3460', borderRadius: '20px',
    cursor: 'pointer', fontSize: '13px',
    color: 'white', fontWeight: 'bold',
  },
  tableBox: {
    background: 'white', borderRadius: '16px',
    padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  },
  inventoryHeader: {
    display: 'flex', justifyContent: 'flex-end', marginBottom: '15px',
  },
  emptyState: { textAlign: 'center', padding: '40px 0' },
  emptyIcon: { fontSize: '40px', margin: '0 0 10px 0' },
  emptyText: { color: '#888', fontSize: '14px', margin: 0 },
  reportTable: { overflowX: 'auto' },
  tableHeader: {
    display: 'flex', padding: '12px 15px',
    background: '#f8f9fa', borderRadius: '8px',
    marginBottom: '5px', fontWeight: 'bold',
    fontSize: '12px', color: '#666', textTransform: 'uppercase',
  },
  tableRow: {
    display: 'flex', padding: '12px 15px',
    borderBottom: '1px solid #f5f5f5',
    fontSize: '13px', color: '#333', alignItems: 'center',
  },
  tableCol: { flex: 1, minWidth: '100px' },
  smallText: { color: '#aaa', fontSize: '11px' },
  statusPill: {
    padding: '3px 10px', borderRadius: '20px',
    fontSize: '11px', fontWeight: 'bold', textTransform: 'capitalize',
  },
  removeBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' },
  returnCard: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '15px',
    background: '#f8f9fa', borderRadius: '10px',
    marginBottom: '10px', flexWrap: 'wrap', gap: '10px',
  },
  returnInfo: { flex: 1 },
  returnName: { margin: '0 0 5px 0', fontWeight: 'bold', color: '#333', fontSize: '14px' },
  returnId: { color: '#888', fontWeight: 'normal', fontSize: '12px' },
  returnInstrument: { margin: '0 0 3px 0', color: '#555', fontSize: '13px' },
  returnCode: { color: '#888', fontSize: '12px' },
  returnDate: { margin: 0, color: '#aaa', fontSize: '12px' },
  returnActions: { display: 'flex', gap: '8px' },
  approveBtn: {
    padding: '8px 16px', background: '#2ecc71',
    color: 'white', border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontWeight: 'bold', fontSize: '13px',
  },
  rejectBtn: {
    padding: '8px 16px', background: '#e74c3c',
    color: 'white', border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontWeight: 'bold', fontSize: '13px',
  },
  loadingText: { textAlign: 'center', color: '#888', padding: '30px' },
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', zIndex: 1001,
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '16px', padding: '30px',
    width: '100%', maxWidth: '480px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    maxHeight: '90vh', overflowY: 'auto',
  },
  modalTitle: { margin: '0 0 20px 0', fontSize: '18px', fontWeight: 'bold', color: '#333' },
  field: { marginBottom: '15px' },
  label: {
    display: 'block', fontSize: '13px',
    fontWeight: '600', color: '#444', marginBottom: '6px',
  },
  input: {
    width: '100%', padding: '11px 14px', borderRadius: '10px',
    border: '1.5px solid #ddd', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box',
  },
  modalButtons: { display: 'flex', gap: '12px', marginTop: '20px' },
  cancelBtn: {
    flex: 1, padding: '12px', background: 'white',
    color: '#666', border: '1.5px solid #ddd',
    borderRadius: '10px', cursor: 'pointer',
    fontWeight: 'bold', fontSize: '14px',
  },
  doneBtn: {
    flex: 1, padding: '12px',
    background: 'linear-gradient(135deg, #0f3460, #16213e)',
    color: 'white', border: 'none', borderRadius: '10px',
    cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
  },
  doneBtnDisabled: {
    flex: 1, padding: '12px', background: '#ccc',
    color: 'white', border: 'none', borderRadius: '10px',
    cursor: 'not-allowed', fontWeight: 'bold', fontSize: '14px',
  },
  memberList: { maxHeight: '250px', overflowY: 'auto', margin: '10px 0' },
  memberSelectBtn: {
    width: '100%', padding: '10px', borderRadius: '8px',
    cursor: 'pointer', display: 'flex',
    alignItems: 'center', gap: '10px',
    marginBottom: '6px', textAlign: 'left',
  },
  memberAvatar: {
    width: '36px', height: '36px', borderRadius: '50%',
    background: '#0f3460', color: 'white',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontWeight: 'bold',
    fontSize: '13px', flexShrink: 0,
  },
  memberName2: { margin: '0 0 2px 0', fontWeight: 'bold', color: '#333', fontSize: '13px' },
  memberId2: { margin: 0, color: '#888', fontSize: '12px' },
  borrowingFor: {
    margin: '0 0 15px 0', fontSize: '14px',
    color: '#555', background: '#f8f9fa',
    padding: '10px', borderRadius: '8px',
  },
  instrumentList: { maxHeight: '200px', overflowY: 'auto', margin: '10px 0' },
  instrumentSelectBtn: {
    width: '100%', padding: '10px 14px',
    borderRadius: '8px', cursor: 'pointer',
    display: 'flex', alignItems: 'center',
    gap: '10px', marginBottom: '6px', textAlign: 'left',
  },
  instrumentCode: {
    fontWeight: 'bold', color: '#0f3460',
    fontSize: '13px', minWidth: '100px',
  },
  instrumentName2: { flex: 1, color: '#333', fontSize: '13px' },
  checkMark: { color: '#0f3460', fontWeight: 'bold', fontSize: '16px' },
  selectedSummary: {
    background: '#e8f0fe', borderRadius: '8px',
    padding: '12px', marginTop: '10px',
  },
  selectedTitle: {
    margin: '0 0 8px 0', fontWeight: 'bold',
    color: '#0f3460', fontSize: '13px',
  },
  selectedItem: { margin: '0 0 3px 0', color: '#333', fontSize: '12px' },
};

export default Instruments;